/**
 * Thin fetch wrapper. Everything the UI knows about the API goes through here:
 * one place for the base URL, the bearer token and error normalisation.
 */
const BASE_URL = import.meta.env.VITE_API_URL || '';
const TOKEN_KEY = 'crm.token';

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (token) => {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
};

export class ApiError extends Error {
  constructor(status, code, message, details) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

/** Subscribers are notified on 401 so the app can log the user out globally. */
const unauthorizedHandlers = new Set();
export const onUnauthorized = (handler) => {
  unauthorizedHandlers.add(handler);
  return () => unauthorizedHandlers.delete(handler);
};

const request = async (method, path, { body, query, signal } = {}) => {
  const url = new URL(`${BASE_URL}/api${path}`, window.location.origin);
  if (query) {
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') url.searchParams.set(key, value);
    });
  }

  const token = getToken();
  let response;
  try {
    response = await fetch(url.toString(), {
      method,
      signal,
      headers: {
        ...(body ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch (err) {
    if (err.name === 'AbortError') throw err;
    throw new ApiError(0, 'NETWORK', 'NETWORK');
  }

  if (response.status === 204) return null;

  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    const error = payload?.error ?? {};
    if (response.status === 401) unauthorizedHandlers.forEach((handler) => handler());
    throw new ApiError(response.status, error.code ?? 'INTERNAL_ERROR', error.message ?? 'Request failed', error.details);
  }

  return payload;
};

/**
 * Same base URL and same bearer token, but the body is returned raw. Used for
 * the endpoints that answer with a file rather than JSON (the CSV sample).
 */
const requestText = async (path) => {
  const token = getToken();
  const response = await fetch(new URL(`${BASE_URL}/api${path}`, window.location.origin).toString(), {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) {
    if (response.status === 401) unauthorizedHandlers.forEach((handler) => handler());
    throw new ApiError(response.status, 'INTERNAL_ERROR', 'Request failed');
  }
  return response.text();
};

export const api = {
  get: (path, options) => request('GET', path, options),
  text: requestText,
  post: (path, body, options) => request('POST', path, { ...options, body }),
  patch: (path, body, options) => request('PATCH', path, { ...options, body }),
  put: (path, body, options) => request('PUT', path, { ...options, body }),
  delete: (path, options) => request('DELETE', path, options),
};
